import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { dataService } from '../../services/DataService'

export const ItemDropdown = ({ value, onChange, onProductSelect, className = '', autoFocus = false, onInputFocus }) => {
  const { t } = useTranslation('lookup')
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  // Either `top` (below the input) or `bottom` (above the input) is set; the other is undefined.
  // Anchoring by `bottom` when flipping keeps a short list glued to the input instead of
  // floating max-height px above it.
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, bottom: undefined, left: 0, width: 0 })
  const dropdownRef = useRef(null)
  const inputRef = useRef(null)
  const optionRefs = useRef([])

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus()
    }
  }, [autoFocus])

  // Initialize selected product based on value
  useEffect(() => {
    if (value && products.length > 0) {
      const product = products.find(p => p.mserp_itemnumber === value)
      setSelectedProduct(product)
    } else if (value) {
      // If we have a value but no matching product, create a placeholder
      setSelectedProduct({ mserp_itemnumber: value, mserp_productsearchname: '' })
    } else {
      setSelectedProduct(null)
    }
  }, [value, products])

  useEffect(() => {
    setHighlightedIndex(products.length > 0 ? 0 : -1)
    optionRefs.current = []
  }, [products])

  useEffect(() => {
    if (isOpen && highlightedIndex >= 0) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' })
    }
  }, [isOpen, highlightedIndex])

  // Debounced search with lazy loading
  useEffect(() => {
    if (searchTerm && searchTerm.length >= 2) {
      const timeoutId = setTimeout(() => {
        loadProducts(searchTerm)
      }, 300) // 300ms debounce

      return () => clearTimeout(timeoutId)
    } else if (searchTerm === '') {
      // Load initial 20 products when dropdown opens without search
      loadProducts('')
    }
  }, [searchTerm])

  const loadProducts = async (search) => {
    setLoading(true)

    try {
      console.log(`Loading products with search: "${search}"`)
      const result = await dataService.getProducts(search, 20) // Limit to 20 items

      if (result.success) {
        console.log(`Loaded ${result.data.length} products for "${search}"`)
        setProducts(result.data)
      } else {
        console.error('Failed to load products:', result.error)
        setProducts([])
      }
    } catch (error) {
      console.error('Error loading products:', error)
      setProducts([])
    } finally {
      setLoading(false)
    }
  }

  // Close dropdown when clicking outside and handle position updates
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    const handleScroll = () => {
      if (isOpen) {
        calculateDropdownPosition()
      }
    }

    const handleResize = () => {
      if (isOpen) {
        calculateDropdownPosition()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      window.addEventListener('scroll', handleScroll, true) // Capture all scroll events
      window.addEventListener('resize', handleResize)

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', handleResize)
      }
    } else {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const calculateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const viewportWidth = window.innerWidth

      // Use viewport-relative positioning instead of absolute positioning
      // This works better within modals and scrolled content
      let top = rect.bottom + 4 // 4px gap below the input
      let left = rect.left
      const width = rect.width

      // Ensure dropdown doesn't go off-screen vertically
      const dropdownMaxHeight = 320 // max-h-80 = 20rem = 320px
      let bottom
      if (top + dropdownMaxHeight > viewportHeight && rect.top > viewportHeight - rect.bottom) {
        // Not enough space below and more space above: anchor the dropdown's bottom edge
        // just above the input (works for any list height)
        bottom = viewportHeight - rect.top + 4
        top = undefined
      }

      // Ensure dropdown doesn't go off-screen horizontally
      const dropdownWidth = Math.max(width, 200) // minWidth is 200px
      if (left + dropdownWidth > viewportWidth) {
        left = viewportWidth - dropdownWidth - 10 // 10px margin from edge
      }

      const position = {
        top: top === undefined ? undefined : Math.max(4, top), // Ensure at least 4px from top of viewport
        bottom,
        left: Math.max(4, left), // Ensure at least 4px from left edge
        width: width
      }

      console.log('Calculating dropdown position:', {
        inputRect: rect,
        viewportSize: { width: viewportWidth, height: viewportHeight },
        finalPosition: position
      })

      setDropdownPosition(position)
    } else {
      console.log('No input ref found for position calculation')
    }
  }

  const handleInputClick = () => {
    console.log('ItemDropdown clicked!')
    calculateDropdownPosition()
    setIsOpen(true)

    // Load initial products if we don't have any and no search term
    if (products.length === 0 && !searchTerm) {
      console.log('Loading initial products...')
      loadProducts('') // Load first 20 products
    }
  }

  const handleInputChange = (e) => {
    const newValue = e.target.value
    setSearchTerm(newValue)
    if (!isOpen) {
      calculateDropdownPosition()
    }
    setIsOpen(true)
  }

  const handleProductSelect = (product) => {
    setSelectedProduct(product)
    onChange(product.mserp_itemnumber)
    onProductSelect?.(product)
    setIsOpen(false)
    setSearchTerm('')
    setHighlightedIndex(-1)
    // Keep the keyboard flow: focus stays on the input so Enter adds the next line
    requestAnimationFrame(() => inputRef.current?.focus())
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
      setHighlightedIndex(-1)
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      setIsOpen(true)
      e.stopPropagation()

      if (products.length > 0) {
        setHighlightedIndex(currentIndex => {
          if (e.key === 'ArrowDown') {
            return Math.min(currentIndex + 1, products.length - 1)
          }
          return Math.max(currentIndex - 1, 0)
        })
      }
    } else if (e.key === 'Enter' && isOpen) {
      e.preventDefault()
      e.stopPropagation()

      if (products[highlightedIndex]) {
        handleProductSelect(products[highlightedIndex])
      }
    }
  }

  const displayValue = selectedProduct
    ? selectedProduct.mserp_itemnumber
    : value || ''

  const dropdownContent = isOpen && (
    <div
      ref={dropdownRef}
      className="fixed bg-white border border-neutral-300 rounded-lg shadow-xl max-h-80 overflow-y-auto"
      style={{
        top: dropdownPosition.top,
        bottom: dropdownPosition.bottom,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        minWidth: '200px',
        zIndex: 100000 // Higher than modal's 99999
      }}
    >
      {loading && (
        <div className="p-3 text-center text-neutral-600">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-primary mx-auto mb-2"></div>
          {t('item.loading')}
        </div>
      )}

      {!loading && products.length === 0 && searchTerm && searchTerm.length >= 2 && (
        <div className="p-3 text-center text-neutral-600">
          {t('item.noResults', { searchTerm })}
        </div>
      )}

      {!loading && products.length === 0 && searchTerm && searchTerm.length < 2 && (
        <div className="p-3 text-center text-neutral-600">
          {t('item.typeMinChars')}
        </div>
      )}

      {!loading && products.length === 0 && !searchTerm && (
        <div className="p-3 text-center text-neutral-600">
          {t('item.loadingInitial')}
        </div>
      )}

      {!loading && products.length > 0 && (
        <div className="py-1">
          {products.map((product, index) => (
            <div
              key={`${product.mserp_itemnumber}-${index}`}
              ref={(element) => { optionRefs.current[index] = element }}
              onMouseDown={(e) => e.preventDefault()} // don't steal focus from the input
              onClick={() => handleProductSelect(product)}
              className={`px-4 py-3 cursor-pointer transition-colors border-b border-neutral-100 last:border-b-0 ${
                highlightedIndex === index ? 'bg-neutral-100' : 'hover:bg-neutral-100'
              }`}
              role="option"
              aria-selected={highlightedIndex === index}
            >
              <div className="font-medium text-neutral-800 text-base">
                {product.mserp_itemnumber}
              </div>
              <div className="text-sm text-neutral-600 mt-1">
                {product.mserp_productsearchname}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <>
      <div className={`relative ${className}`}>
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : displayValue}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          onFocus={onInputFocus}
          className="w-full px-2 py-1 border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
          placeholder={t('item.placeholder')}
        />
      </div>

      {dropdownContent && createPortal(dropdownContent, document.body)}
    </>
  )
}
